import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Container,
  Stack,
  Paper,
} from '@mui/material';
import { 
  CheckCircle, 
  MeetingRoom, 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser } from '../userSlice';
import OnboardingLayout from './OnboardingLayout';
import onboardingService from '../services/onboardingService';

export default function OnboardingCompletionStep() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get onboarding status from Redux instead of separate API call
  const { user } = useSelector((state) => state.user);
  const onboardingStatus = user?.currentOrganization?.onboarding;
  
  const [error, setError] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const completionAttempted = useRef(false);

  // Define required steps
  const REQUIRED_STEPS = useMemo(() => ['welcome', 'billing'], []);

  const areAllRequiredStepsCompleted = useCallback(() => {
    if (!onboardingStatus?.completedSteps) {
      console.log('No completed steps found');
      return false;
    }
    console.log('Checking required steps:', REQUIRED_STEPS);
    console.log('Against completed steps:', onboardingStatus.completedSteps);
    const result = REQUIRED_STEPS.every(step => onboardingStatus.completedSteps.includes(step));
    console.log('All required steps completed?', result);
    return result;
  }, [onboardingStatus, REQUIRED_STEPS]);

  // Auto-complete onboarding when all required steps are done
  useEffect(() => {
    const autoCompleteOnboarding = async () => {
      // Only auto-complete if:
      // 1. We have onboarding status
      // 2. All required steps are completed
      // 3. Onboarding is not already completed
      // 4. We haven't already attempted completion
      if (onboardingStatus && 
          areAllRequiredStepsCompleted() && 
          !onboardingStatus.isCompleted && 
          !completionAttempted.current) {
        
        console.log('Auto-completing onboarding...');
        setIsCompleting(true);
        completionAttempted.current = true;

        try {
          await onboardingService.completeOnboarding();
          console.log('Onboarding auto-completed successfully');
          console.log('Refreshing user data...');
          await dispatch(fetchCurrentUser());
          console.log('User data refreshed');
        } catch (error) {
          console.error('Error auto-completing onboarding:', error);
          setError('Failed to complete onboarding automatically');
          completionAttempted.current = false;
        } finally {
          setIsCompleting(false);
        }
      }
    };

    autoCompleteOnboarding();
  }, [onboardingStatus, dispatch, areAllRequiredStepsCompleted]);

  const shouldShowCompletedView = () => {
    const isCompleted = onboardingStatus?.isCompleted;
    const allRequired = areAllRequiredStepsCompleted();
    console.log('Should show completed view? isCompleted:', isCompleted, 'allRequired:', allRequired);
    return isCompleted || allRequired;
  };

  const handleGoToRooms = () => {
    navigate('/rooms');
  };

  const getStepStatus = (step) => {
    if (!onboardingStatus) return 'pending';
    if (onboardingStatus.completedSteps?.includes(step)) return 'completed';
    if (onboardingStatus.skippedSteps?.includes(step)) return 'skipped';
    return 'pending';
  };

  // Show loading if user data is not yet loaded
  if (!user || !onboardingStatus) {
    return (
      <Container maxWidth="md">
        <Stack spacing={10} alignItems="center">
          <Typography>Loading onboarding status...</Typography>
        </Stack>
      </Container>
    );
  }

  const showCompletedView = shouldShowCompletedView();

  // Data for stepper - matching other onboarding steps
  const steps = [
    { label: "Basic info", active: false, completed: onboardingStatus?.completedSteps?.includes('welcome') },
    { label: "Billing", active: false, completed: onboardingStatus?.completedSteps?.includes('billing') },
    { label: "Complete", active: true, completed: showCompletedView },
  ];

  return (
    <Container maxWidth="md">
      <Stack spacing={10} alignItems="center">
        {/* Header Section */}
        <Stack spacing={6} alignItems="center" sx={{ width: "100%" }}>
          <Stack spacing={2} alignItems="center">
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 500,
                color: 'text.primary',
                textAlign: 'center',
                lineHeight: '65px'
              }}
            >
              {showCompletedView ? "🎉 Welcome to Meeting Rooms!" : "🎯 Complete Your Setup"}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 500,
                color: 'text.secondary',
                textAlign: 'center',
                maxWidth: '538px'
              }}
            >
              {showCompletedView ? "Your workspace is ready to use!" : "You're one step away from completing your onboarding!"}
            </Typography>
          </Stack>

          {/* Progress Stepper */}
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            {steps.map((step, index) => (
              <Stack key={index} alignItems="center" spacing={1}>
                <Paper
                  elevation={0}
                  sx={{
                    width: 48,
                    height: 49,
                    borderRadius: 24,
                    backgroundColor: step.completed || step.active ? 'primary.main' : 'grey.300',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {step.completed && !step.active && (
                    <CheckCircle sx={{ color: 'white', fontSize: 24 }} />
                  )}
                </Paper>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontWeight: step.active || step.completed ? 500 : 400,
                    color: step.active || step.completed ? 'text.primary' : 'text.disabled',
                    textAlign: 'center'
                  }}
                  variant="h6"
                >
                  {step.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Content Section */}
        <Stack spacing={6} alignItems="center" sx={{ width: "100%" }}>
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          {!showCompletedView && (
            <>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  fontWeight: 500,
                  textAlign: 'center'
                }}
              >
                Almost There!
              </Typography>
              
              <Typography
                sx={{
                  fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  color: 'text.secondary',
                  fontSize: 16,
                  textAlign: 'center'
                }}
              >
                Please complete all required steps before finishing your onboarding.
              </Typography>
              
              {/* Show step completion status */}
              <Stack spacing={2} sx={{ width: '100%', maxWidth: 400 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontWeight: 500
                  }}
                >
                  Required Steps:
                </Typography>
                {REQUIRED_STEPS.map(step => (
                  <Box key={step} sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle 
                      sx={{ 
                        mr: 1, 
                        color: getStepStatus(step) === 'completed' ? 'success.main' : 'grey.300' 
                      }} 
                    />
                    <Typography 
                      sx={{ 
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                        textDecoration: getStepStatus(step) === 'completed' ? 'line-through' : 'none',
                        color: getStepStatus(step) === 'completed' ? 'text.secondary' : 'text.primary'
                      }}
                    >
                      {step.charAt(0).toUpperCase() + step.slice(1).replace('-', ' ')} Step
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Button
                variant="contained"
                onClick={() => navigate('/onboarding/welcome')}
                sx={{
                  width: 403,
                  height: 60,
                  backgroundColor: 'primary.main',
                  borderRadius: 1,
                  textTransform: "none",
                  boxShadow: "0px 13px 9.7px -8px rgba(32, 141, 244, 0.24)",
                  fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  fontWeight: 600,
                  fontSize: 16,
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }}
              >
                Go Back to Complete Steps
              </Button>
            </>
          )}

          {showCompletedView && (
            <>
              {/* Auto-completion indicator */}
              {isCompleting && (
                <Alert severity="info" sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                      fontSize: 14
                    }}
                  >
                    Finalizing your onboarding setup...
                  </Typography>
                </Alert>
              )}

              {/* Success Icon */}
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  fontWeight: 500,
                  color: 'success.main',
                  textAlign: 'center',
                  mb: 2
                }}
              >
                Setup Complete!
              </Typography>
              
              <Typography
                sx={{
                  fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  color: 'text.secondary',
                  fontSize: 16,
                  textAlign: 'center',
                  maxWidth: '500px',
                  mb: 4
                }}
              >
                Your meeting room management system is ready. Start creating and managing your rooms.
              </Typography>

              {/* Primary Action Button */}
              <Button
                variant="contained"
                startIcon={<MeetingRoom />}
                onClick={handleGoToRooms}
                sx={{
                  width: 403,
                  height: 60,
                  backgroundColor: 'primary.main',
                  borderRadius: 1,
                  textTransform: "none",
                  boxShadow: "0px 13px 9.7px -8px rgba(32, 141, 244, 0.24)",
                  fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  fontWeight: 600,
                  fontSize: 16,
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }}
              >
                Create Your First Room
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Container>
  );
} 